// src/QRmessaging/QRCreator.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './QRclient.css';

const QRCreator = ({ language, user, onSuccess, SERVER_ROOT }) => {
  const [step, setStep] = useState(1); // 1: 템플릿 선택, 2: 내용 입력, 3: 옵션 설정
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 선택된 항목 및 입력 값
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [messageData, setMessageData] = useState({
    sender_email: user?.email || '',
    receiver_email: '',
    message: '',
    template_id: null,
    message_type: 'text',
    access_type: 'private',
    password: '',
    expiry_days: 30
  });
  
  // 템플릿 목록 가져오기
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${SERVER_ROOT}/api/qr/templates`, {
          params: { is_public: true }
        });
        
        // 기본 템플릿들을 상단에 배치
        const defaultTemplates = response.data.templates.filter(t => t.category === 'default');
        const otherTemplates = response.data.templates.filter(t => t.category !== 'default');
        
        setTemplates([...defaultTemplates, ...otherTemplates]);
      } catch (error) {
        console.error('템플릿 로딩 오류:', error);
        setError(language === 'KR' ? '템플릿을 불러오는 중 오류가 발생했습니다.' : 'Error loading templates.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTemplates();
  }, [SERVER_ROOT, language]);
  
  // 입력 필드 변경 처리
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMessageData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // 템플릿 선택 처리
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setMessageData(prev => ({
      ...prev,
      template_id: template.id,
      message_type: template.category === 'url' ? 'url' : 
                    template.category === 'vcard' ? 'vcard' : 'text'
    }));
    setStep(2);
  };
  
  // 폼 제출 처리
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!messageData.template_id) {
      alert(language === 'KR' ? '템플릿을 선택해주세요.' : 'Please select a template.');
      setStep(1);
      return;
    }
    
    if (!messageData.message) {
      alert(language === 'KR' ? '메시지 내용을 입력해주세요.' : 'Please enter your message.');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${SERVER_ROOT}/api/qr/create`, messageData);
      
      if (response.data && response.data.qrCodeDataUrl) {
        onSuccess({
          url: response.data.url,
          qrCodeDataUrl: response.data.qrCodeDataUrl,
          messageId: response.data.messageId
        });
      }
    } catch (error) {
      console.error('QR 코드 생성 오류:', error);
      setError(language === 'KR' ? 'QR 코드 생성 중 오류가 발생했습니다.' : 'Error creating QR code.');
    } finally {
      setLoading(false);
    }
  };
  
  // 로딩 화면
  if (loading && step === 1) {
    return (
      <div className="qr-creator">
        <div className="qr-loading">
          <div className="qr-spinner"></div>
          <p>{language === 'KR' ? '템플릿 로딩 중...' : 'Loading templates...'}</p>
        </div>
      </div>
    );
  }
  
  // 오류 화면
  if (error && step === 1) {
    return (
      <div className="qr-creator">
        <div className="qr-error">
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="qr-btn"
          >
            {language === 'KR' ? '다시 시도' : 'Try Again'}
          </button>
        </div>
      </div>
    );
  }
  
  // 단계별 렌더링
  return (
    <div className="qr-creator">
      {/* 단계 표시기 */}
      <div className="qr-steps">
        <div className={`qr-step ${step >= 1 ? 'active' : ''}`}>
          {language === 'KR' ? '템플릿 선택' : 'Select Template'}
        </div>
        <div className={`qr-step ${step >= 2 ? 'active' : ''}`}>
          {language === 'KR' ? '내용 입력' : 'Enter Content'}
        </div>
        <div className={`qr-step ${step >= 3 ? 'active' : ''}`}>
          {language === 'KR' ? '옵션 설정' : 'Set Options'}
        </div>
      </div>
      
      {/* 단계 1: 템플릿 선택 */}
      {step === 1 && (
        <div className="qr-template-selection">
          <h2>{language === 'KR' ? '템플릿 선택' : 'Select a Template'}</h2>
          <div className="qr-template-grid">
            {templates.map(template => (
              <div 
                key={template.id}
                className="qr-template-card"
                onClick={() => handleTemplateSelect(template)}
              >
                <div className="qr-template-preview">
                  {/* 템플릿 프리뷰 이미지 또는 아이콘 */}
                  {template.category === 'text' && <i className="fas fa-comment"></i>}
                  {template.category === 'url' && <i className="fas fa-link"></i>}
                  {template.category === 'vcard' && <i className="fas fa-address-card"></i>}
                  {template.category === 'image' && <i className="fas fa-image"></i>}
                  {template.category === 'custom' && <i className="fas fa-palette"></i>}
                </div>
                <h3>{template.name}</h3>
                <p>{template.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* 단계 2: 내용 입력 */}
      {step === 2 && selectedTemplate && (
        <div className="qr-content-input">
          <h2>
            {language === 'KR' ? '내용 입력' : 'Enter Content'}: {selectedTemplate.name}
          </h2>
          
          <form onSubmit={(e) => { e.preventDefault(); setStep(3); }}>
            {/* 템플릿 카테고리에 따른 입력 필드 */}
            {selectedTemplate.category === 'text' && (
              <div className="qr-form-group">
                <label htmlFor="message">
                  {language === 'KR' ? '메시지' : 'Message'}:
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={messageData.message}
                  onChange={handleInputChange}
                  placeholder={language === 'KR' ? '메시지를 입력하세요...' : 'Enter your message...'}
                  rows={5}
                  required
                />
              </div>
            )}
            
            {selectedTemplate.category === 'url' && (
              <div className="qr-form-group">
                <label htmlFor="message">
                  {language === 'KR' ? 'URL' : 'URL'}:
                </label>
                <input
                  type="url"
                  id="message"
                  name="message"
                  value={messageData.message}
                  onChange={handleInputChange}
                  placeholder={language === 'KR' ? 'https://www.example.com' : 'https://www.example.com'}
                  required
                />
                <small>
                  {language === 'KR' ? 'http:// 또는 https://를 포함한 전체 URL을 입력하세요.' : 'Enter the full URL including http:// or https://'}
                </small>
              </div>
            )}
            
            {selectedTemplate.category === 'vcard' && (
              <div className="qr-vcard-form">
                <div className="qr-form-group">
                  <label htmlFor="fullName">
                    {language === 'KR' ? '이름' : 'Full Name'}:
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    onChange={(e) => {
                      const vcardData = JSON.parse(messageData.message || '{}');
                      vcardData.fullName = e.target.value;
                      setMessageData({
                        ...messageData,
                        message: JSON.stringify(vcardData)
                      });
                    }}
                    required
                  />
                </div>
                
                <div className="qr-form-group">
                  <label htmlFor="email">
                    {language === 'KR' ? '이메일' : 'Email'}:
                  </label>
                  <input
                    type="email"
                    id="email"
                    onChange={(e) => {
                      const vcardData = JSON.parse(messageData.message || '{}');
                      vcardData.email = e.target.value;
                      setMessageData({
                        ...messageData,
                        message: JSON.stringify(vcardData)
                      });
                    }}
                  />
                </div>
                
                <div className="qr-form-group">
                  <label htmlFor="phone">
                    {language === 'KR' ? '전화번호' : 'Phone'}:
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    onChange={(e) => {
                      const vcardData = JSON.parse(messageData.message || '{}');
                      vcardData.phone = e.target.value;
                      setMessageData({
                        ...messageData,
                        message: JSON.stringify(vcardData)
                      });
                    }}
                  />
                </div>
                
                <div className="qr-form-group">
                  <label htmlFor="company">
                    {language === 'KR' ? '회사' : 'Company'}:
                  </label>
                  <input
                    type="text"
                    id="company"
                    onChange={(e) => {
                      const vcardData = JSON.parse(messageData.message || '{}');
                      vcardData.company = e.target.value;
                      setMessageData({
                        ...messageData,
                        message: JSON.stringify(vcardData)
                      });
                    }}
                  />
                </div>
                
                <div className="qr-form-group">
                  <label htmlFor="title">
                    {language === 'KR' ? '직함' : 'Title'}:
                  </label>
                  <input
                    type="text"
                    id="title"
                    onChange={(e) => {
                      const vcardData = JSON.parse(messageData.message || '{}');
                      vcardData.title = e.target.value;
                      setMessageData({
                        ...messageData,
                        message: JSON.stringify(vcardData)
                      });
                    }}
                  />
                </div>
                
                <div className="qr-form-group">
                  <label htmlFor="address">
                    {language === 'KR' ? '주소' : 'Address'}:
                  </label>
                  <textarea
                    id="address"
                    onChange={(e) => {
                      const vcardData = JSON.parse(messageData.message || '{}');
                      vcardData.address = e.target.value;
                      setMessageData({
                        ...messageData,
                        message: JSON.stringify(vcardData)
                      });
                    }}
                    rows={3}
                  />
                </div>
              </div>
            )}
            
            <div className="qr-form-actions">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="qr-btn qr-btn-secondary"
              >
                {language === 'KR' ? '이전' : 'Back'}
              </button>
              <button
                type="submit"
                className="qr-btn"
              >
                {language === 'KR' ? '다음' : 'Next'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* 단계 3: 옵션 설정 */}
      {step === 3 && (
        <div className="qr-options-settings">
          <h2>{language === 'KR' ? '옵션 설정' : 'Set Options'}</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="qr-form-group">
              <label htmlFor="receiver_email">
                {language === 'KR' ? '수신자 이메일 (선택사항)' : 'Recipient Email (Optional)'}:
              </label>
              <input
                type="email"
                id="receiver_email"
                name="receiver_email"
                value={messageData.receiver_email}
                onChange={handleInputChange}
                placeholder={language === 'KR' ? '수신자 이메일 (공백 시 누구나 접근 가능)' : 'Recipient email (leave blank for public access)'}
              />
              <small>
                {language === 'KR' ? '지정하지 않으면 누구나 이 메시지에 접근할 수 있습니다.' : 'If not specified, anyone can access this message.'}
              </small>
            </div>
            
            <div className="qr-form-group">
              <label htmlFor="access_type">
                {language === 'KR' ? '접근 설정' : 'Access Type'}:
              </label>
              <select
                id="access_type"
                name="access_type"
                value={messageData.access_type}
                onChange={handleInputChange}
              >
                <option value="private">
                  {language === 'KR' ? '개인용 (수신자만 볼 수 있음)' : 'Private (Only recipient can view)'}
                </option>
                <option value="public">
                  {language === 'KR' ? '공개 (누구나 볼 수 있음)' : 'Public (Anyone can view)'}
                </option>
              </select>
            </div>
            
            <div className="qr-form-group">
              <label htmlFor="password">
                {language === 'KR' ? '비밀번호 (선택사항)' : 'Password (Optional)'}:
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={messageData.password}
                onChange={handleInputChange}
                placeholder={language === 'KR' ? '비밀번호 설정 (선택사항)' : 'Set password (optional)'}
              />
              <small>
                {language === 'KR' ? '설정 시 메시지 열람에 비밀번호가 필요합니다.' : 'If set, a password will be required to view the message.'}
              </small>
            </div>
            
            <div className="qr-form-group">
              <label htmlFor="expiry_days">
                {language === 'KR' ? '만료 기간 (일)' : 'Expiry Period (Days)'}:
              </label>
              <input
                type="number"
                id="expiry_days"
                name="expiry_days"
                value={messageData.expiry_days}
                onChange={handleInputChange}
                min="1"
                max="3650"
              />
              <small>
                {language === 'KR' ? '설정한 기간 이후에는 메시지에 접근할 수 없습니다.' : 'After this period, the message will not be accessible.'}
              </small>
            </div>
            
            <div className="qr-form-actions">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="qr-btn qr-btn-secondary"
              >
                {language === 'KR' ? '이전' : 'Back'}
              </button>
              <button
                type="submit"
                className="qr-btn"
                disabled={loading}
              >
                {loading 
                  ? (language === 'KR' ? '생성 중...' : 'Creating...') 
                  : (language === 'KR' ? 'QR 코드 생성' : 'Create QR Code')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default QRCreator;